import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Modal, Upload, notification, AutoComplete, Checkbox, Tooltip } from 'antd';
import { SendOutlined, InboxOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import axios from './axiosConfig';
import './style.css';

const { Option } = Select;
const { Dragger } = Upload;

const AddForm = ({ onFormSubmit }) => {
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();

  const [company, setCompany]           = useState('auto');
  const [companies, setCompanies]       = useState([]);
  const [text, setText]                 = useState('');
  const [charCount, setCharCount]       = useState(0);
  const [modalOpen, setModalOpen]       = useState(false);
  const [description, setDescription]   = useState('');
  const [attachments, setAttachments]   = useState([]);
  const [screenshot, setScreenshot]     = useState([]);
  const [email, setEmail]               = useState('');
  const [emailOptions, setEmailOptions] = useState([]);
  const [allEmails, setAllEmails]       = useState([]);
  const [priority, setPriority]         = useState(false);
  const [isBugReport, setIsBugReport]   = useState(false);
  const [category, setCategory]         = useState('');
  const [allCategories, setAllCategories]     = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [appUser, setAppUser]           = useState('');
  const [appRole, setAppRole]           = useState('');
  const [appPath, setAppPath]           = useState('');
  const [expectedResult, setExpectedResult] = useState('');
  const [plannedEffort, setPlannedEffort] = useState('');

  useEffect(() => {
    axios.get('/companies')
      .then(r => setCompanies(r.data))
      .catch(() => notification.error({ message: 'Chyba pri načítaní spoločností!' }));

    axios.get('/emails')
      .then(r => { setAllEmails(r.data); setEmailOptions(r.data); })
      .catch(() => {});

    axios.get('/categories')
      .then(r => {
        const opts = (r.data || [])
          .map(c => (typeof c === 'string' ? c : c?.category))
          .filter(v => v)
          .map(v => ({ value: v, label: v }));
        setAllCategories(opts);
        setCategoryOptions(opts);
      })
      .catch(() => {});
  }, []);

  const handleEmailChange = (val) => {
    setEmail(val);
    setEmailOptions(!val ? allEmails : allEmails.filter(e => e && e.toLowerCase().includes(val.toLowerCase())));
  };

  const handleTextChange = (e) => {
    const v = e.target.value;
    if (v.length <= 250) { setText(v); setCharCount(v.length); }
  };

  const handlePaste = (e) => {
    const files = [];
    for (let i = 0; i < e.clipboardData.items.length; i++) {
      const item = e.clipboardData.items[i];
      if (item.kind === 'file') files.push(item.getAsFile());
    }
    if (files.length > 0) {
      const newFiles = files.map(f => ({
        uid: `paste-${Date.now()}-${f.name}`,
        name: f.name || 'screenshot.png',
        status: 'done',
        originFileObj: f,
      }));
      if (isBugReport) {
        setScreenshot(prev => [...prev, ...newFiles]);
      } else {
        setAttachments(prev => [...prev, ...newFiles]);
      }
      notification.success({ message: 'Screenshot pridaný' });
    }
  };

  useEffect(() => {
    if (modalOpen) window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [modalOpen, isBugReport]);

  const buildFormData = () => {
    const formData = new FormData();
    let selectedCompany = company;

    if (company === 'auto' || company === 'custom') {
      const domain = email.split('@')[1] || '';
      const domainWithoutTLD = domain.split('.')[0].toUpperCase();
      const match = companies.find(c => c.domain === domain);
      selectedCompany = match ? match.alias : domainWithoutTLD;
    }

    formData.append('company', selectedCompany);
    formData.append('text', text);
    formData.append('description', description.replace(/\n/g, '<br>'));
    formData.append('email', email);
    formData.append('priority', priority ? '1' : '2');
    if (category) formData.append('category', category);
    formData.append('isBugReport', isBugReport ? '1' : '0');
    if (isBugReport) {
      if (appUser) formData.append('appUser', appUser);
      if (appRole) formData.append('appRole', appRole);
      if (appPath) formData.append('appPath', appPath);
      if (expectedResult) formData.append('expectedResult', expectedResult.replace(/\n/g, '<br>'));
    }
    if (plannedEffort) formData.append('plannedEffort', plannedEffort);

    [...attachments, ...screenshot].forEach(f => {
      formData.append('attachments', f.originFileObj);
    });

    return formData;
  };

  const openModal = () => {
    // Preniesť text z prvého poľa do popisu, ak ešte nie je vyplnený
    if (text && !description.trim()) {
      setDescription(text);
    }
    setModalOpen(true);
  };

  const resetAll = () => {
    form1.resetFields();
    form2.resetFields();
    setCompany('auto'); setText(''); setCharCount(0); setDescription('');
    setAttachments([]); setScreenshot([]); setEmail('');
    setPriority(false); setIsBugReport(false); setCategory('');
    setAppUser(''); setAppRole(''); setAppPath(''); setExpectedResult('');
    setPlannedEffort('');
  };

  const resetPartial = () => {
    setText('');
    setCharCount(0);
    setDescription('');
    setAttachments([]);
    setScreenshot([]);
    setExpectedResult('');
    setAppUser('');
    setAppRole('');
    setAppPath('');
    setPriority(false);
    setIsBugReport(false);
    setPlannedEffort('');
    form1.setFieldsValue({ text: '' });
  };

  const handleSubmit = () => {
    if (!String(text || '').trim()) {
      notification.warning({ message: 'Vyplňte názov / stručný popis.' });
      return;
    }
    Promise.all([form1.validateFields(), form2.validateFields()])
      .then(() => {
        onFormSubmit(buildFormData());
        setModalOpen(false);
        resetAll();
      })
      .catch(info => console.log('Validácia zlyhala:', info));
  };

  const handleSubmitAndContinue = () => {
    if (!String(text || '').trim()) {
      notification.warning({ message: 'Vyplňte názov / stručný popis.' });
      return;
    }
    Promise.all([form1.validateFields(), form2.validateFields()])
      .then(() => {
        onFormSubmit(buildFormData());
        resetPartial();
        notification.success({ message: 'Uložené', description: 'Formulár je pripravený pre ďalší záznam — upravte názov a popis.' });
      })
      .catch(info => console.log('Validácia zlyhala:', info));
  };

  const modalFooter = [
    <Button key="cancel" onClick={() => { setModalOpen(false); resetAll(); }}>
      Zrušiť
    </Button>,
    <Button key="saveNext" onClick={handleSubmitAndContinue} icon={<PlusOutlined />}>
      OK, uložiť ďalší
    </Button>,
    <Button key="ok" type="primary" onClick={handleSubmit}>
      OK
    </Button>,
  ];

  return (
    <>
      <Form form={form1} layout="inline" className="incidentAdd" style={{ display: 'flex', width: '100%' }}>
        <Form.Item style={{ marginRight: '0px' }}>
          <Select
            className="firstInput"
            value={company}
            onChange={setCompany}
            style={{ width: 200 }}
          >
            <Option value="auto">Zistiť automaticky</Option>
            {companies.map(c => <Option key={c.id} value={c.alias}>{c.name}</Option>)}
            <Option value="custom">Moja spoločnosť nie je v zozname</Option>
          </Select>
        </Form.Item>
        <Form.Item style={{ flex: 1, marginRight: '0px' }}>
          <Input
            value={text}
            onChange={handleTextChange}
            style={{ width: '100%', paddingRight: 50 }}
            maxLength={250}
          />
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }}>
            {charCount}/250
          </span>
        </Form.Item>
        <Form.Item style={{ marginRight: 0 }}>
          <Button className="buttonSend" icon={<SendOutlined />} type="primary" onClick={openModal} />
        </Form.Item>
      </Form>

      <Modal
        title="Chcete k svojmu incidentu povedať viac?"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); resetAll(); }}
        footer={modalFooter}
        width={680}
        className="incident-add-modal"
      >
        <Form form={form2} layout="vertical">

          <Form.Item
            label={<span>Názov / stručný popis <span style={{ color: 'red' }}>*</span></span>}
            required
          >
            <Input
              value={text}
              onChange={handleTextChange}
              maxLength={250}
              placeholder="Stručný názov požiadavky"
              suffix={<span style={{ color: '#666', fontSize: 11 }}>{charCount}/250</span>}
            />
          </Form.Item>

          {/* Systém = category */}
          <Form.Item label="Systém / Kategória">
            <AutoComplete
              options={categoryOptions}
              value={category}
              onChange={setCategory}
              onSearch={val => {
                const q = val.toLowerCase();
                setCategoryOptions(
                  q ? allCategories.filter(o => o.value.toLowerCase().includes(q)) : allCategories
                );
              }}
              allowClear
              placeholder="Napr. B2E portál, Škoda SVK Tools…"
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* Detailed description */}
          <Form.Item label="Podrobnejší popis">
            <Input.TextArea
              rows={4}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Form.Item>

          {/* Email */}
          <Form.Item
            label={<span>E-mail <span style={{ color: 'red' }}>*</span></span>}
            name="email"
            rules={[{ required: true, type: 'email', message: 'Zadajte e-mail v správnom formáte.' }]}
          >
            <AutoComplete
              options={emailOptions.map(e => ({ value: e }))}
              onSelect={setEmail}
              onSearch={handleEmailChange}
              value={email}
              filterOption={(input, opt) =>
                (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              <Input type="email" autoComplete="email" />
            </AutoComplete>
          </Form.Item>

          {/* General attachments */}
          <Form.Item label="Prílohy">
            <Dragger
              fileList={attachments}
              beforeUpload={() => false}
              onChange={({ fileList }) => setAttachments(fileList)}
              onRemove={file => setAttachments(prev => prev.filter(f => f.uid !== file.uid))}
              multiple
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Kliknite sem alebo potiahnite súbory</p>
              <p className="ant-upload-hint">Podporovaný je jeden alebo viacero súborov.</p>
            </Dragger>
          </Form.Item>

          {/* Priority checkbox */}
          <Form.Item style={{ marginBottom: 8 }}>
            <Checkbox checked={priority} onChange={e => setPriority(e.target.checked)}>
              Je tento incident prioritný?{' '}
              <Tooltip title="Prioritný ticket sa považuje taký, kde je prekročený dohodnutý zmluvný čas o dostupnosti služby alebo aplikácie.">
                <InfoCircleOutlined />
              </Tooltip>
            </Checkbox>
          </Form.Item>

          {/* Planned effort */}
          <Form.Item label="Náročnosť plánovaná (MD)" style={{ marginBottom: 8 }}>
            <Input
              type="number"
              step="0.5"
              min="0"
              value={plannedEffort}
              onChange={e => setPlannedEffort(e.target.value)}
              placeholder="napr. 1.5"
              style={{ maxWidth: 140 }}
            />
          </Form.Item>

          {/* Bug report checkbox */}
          <Form.Item style={{ marginBottom: isBugReport ? 16 : 0 }}>
            <Checkbox checked={isBugReport} onChange={e => setIsBugReport(e.target.checked)}>
              Nahlásiť ako zmenovú požiadavku / bug
            </Checkbox>
          </Form.Item>

          {/* Bug report extra fields */}
          {isBugReport && (
            <div style={{ background: '#1e1f22', border: '1px solid #2e2f33', borderRadius: 6, padding: '14px 16px', marginTop: 4 }}>
              <Form.Item label="Prihlásený užívateľ v aplikácii" style={{ marginBottom: 10 }}>
                <Input value={appUser} onChange={e => setAppUser(e.target.value)} placeholder="napr. admin@firma.sk" />
              </Form.Item>
              <Form.Item label="Použitá rola v aplikácii" style={{ marginBottom: 10 }}>
                <Input value={appRole} onChange={e => setAppRole(e.target.value)} placeholder="napr. ADMIN, USER, MANAGER…" />
              </Form.Item>
              <Form.Item label="Cesta v aplikácii" style={{ marginBottom: 10 }}>
                <Input value={appPath} onChange={e => setAppPath(e.target.value)} placeholder="napr. /dashboard/settings" />
              </Form.Item>
              <Form.Item label="Screenshot (drag & drop / paste)" style={{ marginBottom: 10 }}>
                <Dragger
                  fileList={screenshot}
                  beforeUpload={() => false}
                  accept="image/*"
                  onChange={({ fileList }) => setScreenshot(fileList)}
                  onRemove={file => setScreenshot(prev => prev.filter(f => f.uid !== file.uid))}
                  multiple
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">Potiahnite screenshot sem alebo vložte (Ctrl+V)</p>
                  <p className="ant-upload-hint">Podporované formáty: PNG, JPG, GIF, WebP</p>
                </Dragger>
              </Form.Item>
              <Form.Item label="Očakávaný výsledok" style={{ marginBottom: 0 }}>
                <Input.TextArea
                  rows={3}
                  value={expectedResult}
                  onChange={e => setExpectedResult(e.target.value)}
                  placeholder="Čo by sa malo stať / aký bol očakávaný výsledok?"
                />
              </Form.Item>
            </div>
          )}

        </Form>
      </Modal>
    </>
  );
};

export default AddForm;
